import { FlowExportSAATemplateFields } from "../types/flowTypes";
import { Destination } from "../types/types";

export const FLOW_EXPORT_SAA_TEMPLATE_FIELDS = "FLOW_EXPORT_SAA_TEMPLATE_FIELDS";

import makeReducerFor from "./_genericDbReducer";
import { createSelector } from "reselect";
import { getFlowItemsForSelectedFlow } from "./flowItems";
import { getFlowExportsForSelectedFlow, getFlowExportsArray } from "./flowExports";
import type { FlowItem, FlowErrorsByItemId, FlowExport } from "../types/stores/flowTypes";

const myGenericReducer = makeReducerFor("FLOW_EXPORT_SAA_TEMPLATE_FIELDS", "FlowExportSAATemplateFieldId");
import subItemReducer from "./_genericFlowSubItemReducer";
import { IAppState } from "../types/stores/index";
import { ISAAValidationRule } from "types/stores/companyTable";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const myReducer = (state = {}, action) => subItemReducer(myGenericReducer(state, action), action);
export default myReducer;

///////// SELECTORS /////////////

export const getFlowExportSAATemplateFieldsArray = createSelector(
    (state: IAppState) => state.flowExportSAATemplateFields.byId,
    (flowExportSAATemplateFieldsById: {
        [number: number]: FlowExportSAATemplateFields;
    }): Array<FlowExportSAATemplateFields> => {
        const r: Array<FlowExportSAATemplateFields> = Object.values(flowExportSAATemplateFieldsById);
        return r;
    }
);

export type FlowExportSAATemplateFieldsByItemId = {
    [number: number]: Array<FlowExportSAATemplateFields>;
};

export const getFlowExportSAATemplateFieldsByFlowItemId = createSelector(
    (state: IAppState) => getFlowExportSAATemplateFieldsArray(state),
    (flowExportSAATemplateFields: Array<FlowExportSAATemplateFields>): FlowExportSAATemplateFieldsByItemId =>
        flowExportSAATemplateFields.reduce((acc, row) => {
            if (acc[row.FlowItemId] == null) {
                acc[row.FlowItemId] = [];
            }
            acc[row.FlowItemId].push(row);
            return acc;
        }, {})
);

export const getFlowExportSAATemplateFieldsForSelectedFlow = createSelector(
    (state: IAppState) => getFlowItemsForSelectedFlow(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsByFlowItemId(state),
    (
        flowItems: Array<FlowItem>,
        flowExportSAATemplateFieldsByItemId: FlowExportSAATemplateFieldsByItemId
    ): Array<FlowExportSAATemplateFields> => {
        let result: Array<FlowExportSAATemplateFields> = [];
        const itemIds = flowItems.map(fi => fi.FlowItemId);
        for (const itemId of itemIds) {
            const templateFields = flowExportSAATemplateFieldsByItemId[itemId];
            if (templateFields != null) {
                result = result.concat(templateFields);
            }
        }
        return result;
    }
);

export const getFlowExportSAATemplateFieldsForNewFlow = createSelector(
    (state: IAppState) => getFlowItemsForSelectedFlow(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsByFlowItemId(state),
    (state: IAppState) => state.vars.saaTapadDeliveryTypes || [],
    (
        flowItems: Array<FlowItem>,
        flowExportSAATemplateFieldsByItemId: FlowExportSAATemplateFieldsByItemId,
        saaTapadDeliveryTypes: Array<any>
    ): Array<FlowExportSAATemplateFields> => {
        let result: Array<FlowExportSAATemplateFields> = [];
        const itemIds = flowItems.map(fi => fi.FlowItemId);
        for (const itemId of itemIds) {
            const templateFieldsList = flowExportSAATemplateFieldsByItemId[itemId];
            if (templateFieldsList != null) {
                templateFieldsList.forEach(templateFields => {
                    // Fetch segment fields and validation rules specific to the template ID
                    const saaSegmentFields =
                        saaTapadDeliveryTypes.find(
                            item => Number(item.SAATemplate.TemplateId) == Number(templateFields.TemplateId)
                        )?.SAASegmentFields || [];
                    const fieldValues = JSON.parse(templateFields.ValueJSON);

                    // Fast lookup by FieldName
                    const defaultValueByFieldName = new Map(
                        saaSegmentFields.map(s => [s.SAASegmentField.FieldName, s.SAASegmentField.DefaultValue])
                    );

                    // // Example: get DefaultValue for activationChannelId
                    // const activationDefault = defaultValueByFieldName.get("activationChannelId"); // "{ActivationChannelId}"

                    // If you want to iterate all fieldValues and pull matching DefaultValue:
                    const merged = Object.keys(fieldValues).map(fieldName => ({
                        fieldName,
                        value: fieldValues[fieldName as keyof typeof fieldValues],
                        defaultValue: defaultValueByFieldName.get(fieldName) ?? null,
                    }));

                    const placeholderTokens = ["{ParentFlowItemID}", "{FlowItemID}", "{FlowID}"];

                    const cleanedValues = merged.reduce<Record<string, string>>(
                        (acc, { fieldName, value, defaultValue }) => {
                            const hasPlaceholder =
                                typeof defaultValue === "string" &&
                                placeholderTokens.some(token => defaultValue.includes(token));

                            acc[fieldName] = hasPlaceholder ? "" : String(value ?? "");
                            return acc;
                        },
                        {}
                    );

                    const updatedTemplateFields: FlowExportSAATemplateFields = {
                        ...templateFields,
                        ValueJSON: JSON.stringify(cleanedValues),
                    };

                    result = result.concat(updatedTemplateFields);
                });
            }
        }
        return result;
    }
);

const saaTemplateFieldsToErrorsById = (
    flowExports: Array<FlowExport>,
    flowExportSAATemplateFields: Array<FlowExportSAATemplateFields>,
    destinations: Array<any>,
    saaTapadDeliveryTypes: Array<any>
): FlowErrorsByItemId => {
    const errorsById = {};
    for (const flowExport of flowExports) {
        errorsById[flowExport.FlowItemId] = validateExportSAATemplateFields(
            flowExport,
            flowExportSAATemplateFields,
            destinations,
            saaTapadDeliveryTypes
        );
    }
    return errorsById;
};

export const getExportSAATemplateFieldErrorsForSelectedFlow = createSelector(
    (state: IAppState) => getFlowExportsForSelectedFlow(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsForSelectedFlow(state),
    (state: IAppState) => state.vars.destinations,
    (state: IAppState) => state.vars.saaTapadDeliveryTypes || [],
    saaTemplateFieldsToErrorsById
);

export const getExportSAATemplateFieldErrorsForAllFlows = createSelector(
    (state: IAppState) => getFlowExportsArray(state),
    (state: IAppState) => getFlowExportSAATemplateFieldsArray(state),
    (state: IAppState) => state.vars.destinations,
    (state: IAppState) => state.vars.saaTapadDeliveryTypes || [],
    saaTemplateFieldsToErrorsById
);

/**
 * Applies numeric validation based on the specified rule type, numeric value, and threshold.
 *
 * @param {string} ruleType - The type of validation rule to apply.
 *                            Supported values include:
 *                            - "MIN_ALLOWED": Checks if the numeric value is less than the threshold.
 *                            - "MAX_ALLOWED": Checks if the numeric value is greater than the threshold.
 *                            - "GREATER_THAN": Checks if the numeric value is less than or equal to the threshold.
 *                            - "LESS_THAN": Checks if the numeric value is greater than or equal to the threshold.
 *                            - "EQUAL": Checks if the numeric value is not equal to the threshold.
 *                            - "LESS_THAN_OR_EQUAL_TO": Checks if the numeric value is greater than the threshold.
 *                            - "GREATER_THAN_OR_EQUAL_TO": Checks if the numeric value is less than the threshold.
 *                            - "MOD": Checks if the numeric value is not divisible by the threshold.
 *                            - "IS_INT": Checks if the numeric value is not an integer.
 * @param {number} numericValue - The numeric value to validate.
 * @param {number} threshold - The threshold value used for validation.
 * @returns {boolean} - Returns `true` if the validation condition is met, otherwise `false`.
 */
export const applyNumericValidation = (ruleType: string, numericValue: number, threshold: number): boolean => {
    if (ruleType == null) {
        return false;
    }

    const normalizedRuleType = ruleType.trim().toUpperCase();
    const ruleConditions = {
        MIN_ALLOWED: numericValue < threshold,
        MAX_ALLOWED: numericValue > threshold,
        GREATER_THAN: numericValue <= threshold,
        LESS_THAN: numericValue >= threshold,
        EQUAL: numericValue !== threshold,
        LESS_THAN_OR_EQUAL_TO: numericValue > threshold,
        GREATER_THAN_OR_EQUAL_TO: numericValue < threshold,
        MOD: numericValue % threshold !== 0,
        IS_INT: !Number.isInteger(numericValue),
    };

    return ruleConditions[normalizedRuleType] || false;
};

/**
 * Validates SAA Template fields based on destination, segment field visibility and validation rules.
 *
 * @param {FlowExport} flowExport - The current export flow object.
 * @param {Array<FlowExportSAATemplateFields>} flowExportSAATemplateFields - The template fields associated with the export flow.
 * @param {Array<Destination>} destinations - List of available destinations.
 * @param {Array<any>} saaTapadDeliveryTypes - SAA segement fields and Validation rules for that delivery type.
 * @returns {Array<string>} - List of validation error messages.
 */
export const validateExportSAATemplateFields = (
    flowExport: FlowExport,
    flowExportSAATemplateFields: Array<FlowExportSAATemplateFields>,
    destinations: Array<Destination>,
    saaTapadDeliveryTypes: Array<any>
): Array<string> => {
    const errors: Array<string> = [];
    const errorFields: Array<string> = [];

    if (!destinations || destinations.length === 0) {
        return errors;
    }

    const destination = destinations.find(x => x.PartnerAccessId === flowExport.DestinationId);
    if (!destination) {
        return errors;
    }

    // check if SAADeploySetting is set
    if (!destination.SAADeploySetting || destination.SAADeploySetting == 0) {
        return errors;
    }

    const templateFieldsList = flowExportSAATemplateFields.filter(x => x.FlowItemId === flowExport.FlowItemId);

    templateFieldsList.forEach(templateFields => {
        if (!templateFields.TemplateId) {
            errors.push("SAA Template ID is required");
            return;
        }

        // Fetch segment fields and validation rules specific to the template ID
        const saaSegmentFields =
            saaTapadDeliveryTypes.find(item => Number(item.SAATemplate.TemplateId) == Number(templateFields.TemplateId))
                ?.SAASegmentFields || [];

        try {
            const fieldValues = JSON.parse(templateFields.ValueJSON);
            const obj = Object.entries(fieldValues);

            // Extract fieldName from saaSegmentFields
            const fieldNames = saaSegmentFields.map(f => f.SAASegmentField.FieldName);
            const existingKeys = obj.map(([key, _]) => key);

            // Add any missing displaynames to obj for compatibility with older exports
            fieldNames.forEach(name => {
                if (!existingKeys.includes(name)) {
                    obj.push([name, ""]);
                }
            });
            saaSegmentFields.forEach(({ SAASegmentField, SAAValidationRules }) => {
                // Skip validation if field is not editable
                if (!SAASegmentField.IsEditable) return;

                // Find field match based on its visibility
                const keyMatch = obj.find(([key]) => key === SAASegmentField.FieldName && SAASegmentField.IsVisible);

                if (!keyMatch) return;

                const [fieldName, value] = keyMatch;

                // Check if field is required and value is empty or invalid
                if (SAASegmentField.Required && ["", "NaN", null].includes(String(value))) {
                    if (SAASegmentField.DefaultValue != null && SAASegmentField.DefaultValue.includes("{")) {
                        // Exclude fields with transformations (those containing '{')
                        return;
                    }
                    errorFields.push(fieldName);
                }

                if (!value || !SAAValidationRules.length) return;

                const ruleValidates = ({ RuleType, ValidatorRegex, Value }: ISAAValidationRule): boolean => {
                    if (["REGEX_VALIDATOR", "REGEX"].includes(RuleType)) {
                        return new RegExp(ValidatorRegex!).test(String(value));
                    }

                    const threshold = parseFloat(String(Value)); // Value is a field of ISAAValidationRule interface
                    const numericValue = parseFloat(String(value));
                    if (isNaN(threshold) || isNaN(numericValue)) return true; // can't evaluate -> don't fail it here

                    return !applyNumericValidation(RuleType, numericValue, threshold);
                };

                // NOTE: ChildrenRules is not populated by the backend today
                // so we group the flat SAAValidationRules array by ParentValidationRuleId ourselves
                const rules = SAAValidationRules as Array<ISAAValidationRule>;
                const orGroupRoots = rules.filter(
                    (r: ISAAValidationRule) => r.RuleType == "OR" && r.ParentValidationRuleId == null
                );
                const orGroupIds = new Set(orGroupRoots.map(r => r.ValidationRuleId));
                const childRulesByParentId = rules.reduce<Record<number, Array<ISAAValidationRule>>>(
                    (acc, r: ISAAValidationRule) => {
                        if (r.ParentValidationRuleId != null && orGroupIds.has(r.ParentValidationRuleId)) {
                            if (!acc[r.ParentValidationRuleId]) {
                                acc[r.ParentValidationRuleId] = [];
                            }
                            acc[r.ParentValidationRuleId].push(r);
                        }
                        return acc;
                    },
                    {}
                );
                const standaloneRules = rules.filter(
                    (r: ISAAValidationRule) => r.ParentValidationRuleId == null && r.RuleType != "OR"
                );

                // Existing flat-rule behavior, unchanged, scoped to standalone rules only
                standaloneRules.forEach((rule: ISAAValidationRule) => {
                    if (rule.Severity != "ERROR") return;

                    if (!ruleValidates(rule)) {
                        errors.push(`${SAASegmentField.DisplayName} : ${rule.Message}`);
                    }
                });
                // OR-group evaluation: field passes if at least one ERROR-severity child rule passes
                orGroupRoots.forEach(orRow => {
                    const errorChildren = (childRulesByParentId[orRow.ValidationRuleId] || []).filter(
                        c => c.Severity == "ERROR"
                    );

                    if (!errorChildren.length) return;

                    const groupPassed = errorChildren.some(ruleValidates);

                    if (!groupPassed) {
                        errors.push(`${SAASegmentField.DisplayName} : ${orRow.Message}`);
                    }
                });
            });
        } catch (e) {
            errors.push("Invalid JSON format in SAA Template Field values");
        }
    });

    if (errorFields.length > 0) {
        const errorFieldString = errorFields.map(field => `'${field}'`).join(", ");
        errors.push(`The following SAA Template fields are required: ${errorFieldString}.`);
    }

    return errors;
};
